import { useEffect, useState } from 'react'
import { Suspense } from 'react'
import { FormattedMessage } from 'react-intl'
import { useAppSelector } from 'universal-scripts'
import type {
  BlueprintNode,
  FilterNode,
  SelectNode,
  GroupNode,
  JoinNode,
  OperationType,
  SortNode,
  PivotNode
} from 'src/types/queries'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import OperationErrorMessage from './OperationErrorMessage'
import Loader from '@components/Loader/Loader'
import FilterEditor from './FilterEditor/FilterEditor'
import SelectEditor from './SelectEditor/SelectEditor'
import GroupEditor from './GroupEditor/GroupEditor'
import JoinEditor from './JoinEditor/JoinEditor'
import SortEditor from './SortEditor/SortEditor'
import PivotEditor from './PivotEditor/PivotEditor'
import OperationPicker from './OperationPicker'
import { buildEmptyOperationNode } from '@utils/blueprintUtils'

import './OperationEditor.css'

type Step = 'pick' | 'detail'

interface OperationEditorProps {
  mode: 'add' | 'edit'
  initialNode: BlueprintNode | null
  nodeToWrap: BlueprintNode | null
  onChangeNode: (node: BlueprintNode | null) => void
}

export default function OperationEditor({
  mode,
  initialNode,
  nodeToWrap,
  onChangeNode
}: OperationEditorProps) {
  const [step, setStep] = useState<Step>(mode === 'edit' ? 'detail' : 'pick')
  const [selectedType, setSelectedType] = useState<OperationType | null>(null)
  const [workingNode, setWorkingNode] = useState<BlueprintNode | null>(
    mode === 'edit' ? initialNode : null
  )

  const blueprint = useAppSelector((state) => state.query.draft.blueprint || [])

  useEffect(() => {
    onChangeNode(mode === 'edit' ? initialNode : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSelectType = (type: OperationType) => {
    setSelectedType(type)
  }

  const handleContinue = () => {
    if (selectedType && nodeToWrap) {
      const currentIdx = blueprint.findIndex(
        (n) => n.nodeId === nodeToWrap.nodeId
      )
      const rightNeighbor =
        currentIdx !== -1 && currentIdx + 1 < blueprint.length
          ? blueprint[currentIdx + 1]
          : null
      const newNode = buildEmptyOperationNode(
        selectedType,
        nodeToWrap,
        rightNeighbor
      )
      setWorkingNode(newNode)
      onChangeNode(newNode)
      setStep('detail')
    }
  }

  const handleBack = () => {
    setWorkingNode(null)
    onChangeNode(null)
    setStep('pick')
  }

  const handleNodeChange = (node: BlueprintNode) => {
    setWorkingNode(node)
    onChangeNode(node)
  }

  if (step === 'pick') {
    return (
      <OperationPicker
        blueprint={blueprint}
        nodeToWrap={nodeToWrap}
        selectedType={selectedType}
        onSelectType={handleSelectType}
        onContinue={handleContinue}
      />
    )
  }

  if (workingNode === null) {
    return null
  }

  return (
    <div id="operation-editor">
      {workingNode.type === 'filter' && (
        <ErrorBoundary errorComponent={OperationErrorMessage}>
          <Suspense fallback={<Loader mini />}>
            <FilterEditor
              node={workingNode as FilterNode}
              onChange={handleNodeChange}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {workingNode.type === 'select' && (
        <ErrorBoundary errorComponent={OperationErrorMessage}>
          <Suspense fallback={<Loader mini />}>
            <SelectEditor
              node={workingNode as SelectNode}
              onChange={handleNodeChange}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {workingNode.type === 'group' && (
        <ErrorBoundary errorComponent={OperationErrorMessage}>
          <Suspense fallback={<Loader mini />}>
            <GroupEditor
              node={workingNode as GroupNode}
              onChange={handleNodeChange}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {workingNode.type === 'join' && (
        <ErrorBoundary errorComponent={OperationErrorMessage}>
          <Suspense fallback={<Loader mini />}>
            <JoinEditor
              node={workingNode as JoinNode}
              onChange={handleNodeChange}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {workingNode.type === 'sort' && (
        <ErrorBoundary errorComponent={OperationErrorMessage}>
          <Suspense fallback={<Loader mini />}>
            <SortEditor
              node={workingNode as SortNode}
              onChange={handleNodeChange}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {workingNode.type === 'pivot' && (
        <ErrorBoundary errorComponent={OperationErrorMessage}>
          <Suspense fallback={<Loader mini />}>
            <PivotEditor
              node={workingNode as PivotNode}
              onChange={handleNodeChange}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {mode === 'add' && (
        <footer>
          <button className="delete-button" onClick={handleBack}>
            <span className="icon icon-angle-left" />
            <FormattedMessage id="node-editor.button.back" />
          </button>
        </footer>
      )}
    </div>
  )
}
